import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import formatInterviewReference from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/formatInterviewReference';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from '@verclam/synapse';
import useSubjectDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectDocuments';
import useSubjectImportedDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectImportedDocuments';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';

const summaryStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const listStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const chipStyle = css`
	display: inline-block;
	padding: 0.25rem 0.75rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.accent};
`;

type SourceChip = {
	readonly key: string;
	readonly label: string;
	/** `null` for a source with no screen of its own (an imported document). */
	readonly to: string | null;
};

/** The sources the open document was generated from, in the server's order, one link chip each.
 * Interview dates come from `interviewsStore`, document titles and types from
 * `documentsStore.documents`, imported-document names from `importedDocumentsStore` (no link: no screen); each falls back to "n° {id}" while unresolved. Renders
 * nothing without a source. */
const DocumentSourcesSummary: React.FC = () => {
	const { subject } = useDocumentRoute();
	const document = useSelector((state) => state.documentsStore.document);
	const { data: documents } = useSubjectDocuments(subject);
	const { data: interviews } = useSubjectInterviews(subject);
	const { data: importedDocuments } = useSubjectImportedDocuments(subject);
	const { documentTypes } = useAccountDocumentTypes();

	const chips = React.useMemo((): readonly SourceChip[] => {
		if (document === null) {
			return [];
		}

		return document.sources.map((source): SourceChip => {
			if (source.kind === 'interview') {
				return {
					key: `interview-${source.id}`,
					label: formatInterviewReference(interviews?.findById(source.id) ?? null, source.id),
					to: `${buildSubjectPath(subject)}/interviews/${source.id}`,
				};
			}

			if (source.kind === 'imported_document') {
				const importedDocument = importedDocuments?.findById(source.id) ?? null;

				return {
					key: `imported-document-${source.id}`,
					label:
						importedDocument === null
							? `Document importé n° ${source.id}`
							: `${importedDocument.name} — Document importé`,
					to: null,
				};
			}

			const sourceDocument = documents?.findById(source.id) ?? null;
			const typeLabel =
				sourceDocument !== null && documentTypes !== null
					? documentTypes.labelOf(sourceDocument.documentTypeId)
					: null;
			const label =
				sourceDocument === null
					? `Document n° ${source.id}`
					: `${sourceDocument.title}${typeLabel === null ? '' : ` — ${typeLabel}`}`;

			return {
				key: `document-${source.id}`,
				label,
				to: `${buildSubjectPath(subject)}/documents/${source.id}`,
			};
		});
	}, [document, documents, interviews, importedDocuments, documentTypes, subject]);

	if (chips.length === 0) {
		return null;
	}

	return (
		<div css={summaryStyle}>
			<p css={eyebrowStyle}>SOURCES</p>
			<ul css={listStyle}>
				{chips.map((chip) => (
					<li key={chip.key}>
						<ConditionalLoader
							data={chip.to}
							fallback={<span css={chipStyle}>{chip.label}</span>}
						>
							{(path) => (
								<Link css={chipStyle} to={path}>
									{chip.label}
								</Link>
							)}
						</ConditionalLoader>
					</li>
				))}
			</ul>
		</div>
	);
};

export default DocumentSourcesSummary;
